import { Controller } from 'stimulus';
import Modal from 'bootstrap/js/src/modal';
import { createPopper } from '@popperjs/core';

// Generic "update notice" toast + optional details modal. The dismissal key
// and the optional anchor selector are supplied per-notice via Stimulus values,
// so multiple independent notices can coexist on a page.
//
// When an `anchorSelector` is given, the toast is positioned against that
// element with Popper — the same engine Bootstrap tooltips/popovers use — so it
// auto-flips and shifts to stay on screen rather than clipping (no manual
// breakpoints, no DOM re-parenting). `placement` picks the preferred side
// (top/bottom/left/right); Popper flips to the opposite side when there's no
// room and the arrow follows. Without an anchor the toast renders inline where
// it's placed.
export default class extends Controller {
  static targets = ['toast', 'modal', 'arrow'];

  static values = {
    storageKey: String,
    anchorSelector: String,
    placement: { type: String, default: 'top' },
  };

  connect() {
    if (localStorage.getItem(this.storageKeyValue)) {
      this.element.remove();
      return;
    }

    this.toastTarget.removeAttribute('hidden');
    this.positionToast();
  }

  disconnect() {
    this.popper?.destroy();
    this.popper = null;
  }

  close() {
    this.element.remove();
  }

  showDetails() {
    const modal = Modal.getOrCreateInstance(this.modalTarget);
    modal.show();
  }

  dismiss() {
    localStorage.setItem(this.storageKeyValue, 'true');
    this.element.remove();
  }

  positionToast() {
    if (!this.anchorSelectorValue) return;

    const anchor = document.querySelector(this.anchorSelectorValue);
    if (!anchor) return;

    // `flip` and `preventOverflow` are Popper defaults; leaving `flip`
    // unconfigured lets it fall back to the opposite side on whichever axis the
    // preferred placement sits on (top↔bottom, left↔right), which the arrow CSS
    // handles for all four edges.
    const modifiers = [
      { name: 'offset', options: { offset: [0, 10] } },
      { name: 'preventOverflow', options: { padding: 8 } },
    ];

    if (this.hasArrowTarget) {
      modifiers.push({
        name: 'arrow',
        options: { element: this.arrowTarget, padding: 8 },
      });
    }

    this.popper = createPopper(anchor, this.toastTarget, {
      placement: this.placementValue,
      modifiers,
    });
  }
}
